/**
 * Global app state (auth, theme, calendars, events, widgets).
 */
import { create } from 'zustand';
import {
  authApi,
  calendarApi,
  notesApi,
  widgetApi,
  alertApi,
  setToken,
  getToken,
  type PublicUser,
  type Calendar,
  type CalendarEvent,
  type StickyNote,
  type Todo,
  type Message,
  type WidgetLayoutItem,
  type FamilyAlert,
  type AlertLevel,
} from '../lib/api';
import { getDeviceProfile, getLayoutProfile } from '../lib/realtime';

interface AppState {
  ready: boolean;
  user: PublicUser | null;
  theme: 'dark' | 'light';
  calendars: Calendar[];
  events: CalendarEvent[];
  stickies: StickyNote[];
  todos: Todo[];
  messages: Message[];
  users: PublicUser[];
  layout: WidgetLayoutItem[];
  layoutEditMode: boolean;
  selectedDate: string | null;
  dayPanelOpen: boolean;
  eventEditor: {
    open: boolean;
    event: CalendarEvent | null;
    defaultDate?: string;
  };
  /** Active family alert overlays (highest severity first) */
  activeAlerts: FamilyAlert[];
  composeAlertOpen: boolean;
  familyName: string;
  connected: boolean;
  kiosk: boolean;

  init: () => Promise<void>;
  loginPin: (pin: string) => Promise<void>;
  loginUser: (email: string, password: string) => Promise<void>;
  logout: () => void;
  setTheme: (t: 'dark' | 'light') => void;
  toggleTheme: () => void;
  refreshCalendars: () => Promise<void>;
  refreshEvents: (from?: string, to?: string) => Promise<void>;
  refreshWidgetsData: () => Promise<void>;
  refreshLayout: () => Promise<void>;
  refreshAlerts: () => Promise<void>;
  saveLayout: (layout: WidgetLayoutItem[]) => Promise<void>;
  setLayoutEditMode: (v: boolean) => void;
  openDay: (date: string) => void;
  closeDay: () => void;
  openEventEditor: (event?: CalendarEvent | null, defaultDate?: string) => void;
  closeEventEditor: () => void;
  setComposeAlertOpen: (v: boolean) => void;
  sendAlert: (level: AlertLevel, body: string, title?: string) => Promise<void>;
  dismissAlert: (id: string) => Promise<void>;
  setConnected: (v: boolean) => void;
  applyRealtime: (type: string, payload: unknown) => void;
}

function applyTheme(theme: 'dark' | 'light') {
  document.documentElement.classList.toggle('dark', theme === 'dark');
  document.documentElement.classList.toggle('light', theme === 'light');
  localStorage.setItem('fink_theme', theme);
}

function isKiosk(): boolean {
  const p = new URLSearchParams(window.location.search);
  return p.get('kiosk') === '1' || p.get('tv') === '1';
}

function monthRange(d = new Date()) {
  const from = new Date(d.getFullYear(), d.getMonth() - 1, 1);
  const to = new Date(d.getFullYear(), d.getMonth() + 2, 0, 23, 59, 59);
  return { from: from.toISOString(), to: to.toISOString() };
}

export const useAppStore = create<AppState>((set, get) => ({
  ready: false,
  user: null,
  theme: (localStorage.getItem('fink_theme') as 'dark' | 'light') || 'dark',
  calendars: [],
  events: [],
  stickies: [],
  todos: [],
  messages: [],
  users: [],
  layout: [],
  layoutEditMode: false,
  selectedDate: null,
  dayPanelOpen: false,
  eventEditor: { open: false, event: null },
  activeAlerts: [],
  composeAlertOpen: false,
  familyName: 'Fink Family',
  connected: false,
  kiosk: isKiosk(),

  init: async () => {
    applyTheme(get().theme);
    try {
      if (getToken()) {
        const { user } = await authApi.me();
        set({ user });
      }
    } catch {
      setToken(null);
      set({ user: null });
    }

    try {
      const [cals, usersRes, settingsRes] = await Promise.all([
        calendarApi.list(),
        authApi.users(),
        widgetApi.settings().catch(() => ({ settings: {} as Record<string, string> })),
      ]);
      const settings: Record<string, string> = settingsRes.settings || {};
      set({
        calendars: cals.calendars,
        users: usersRes.users,
        familyName: settings.family_name || 'Fink Family',
      });
      if (settings.theme === 'light' || settings.theme === 'dark') {
        get().setTheme(settings.theme);
      }
      const range = monthRange();
      await Promise.all([
        get().refreshEvents(range.from, range.to),
        get().refreshWidgetsData(),
        get().refreshLayout(),
        get().refreshAlerts(),
      ]);
    } catch (err) {
      console.error('Init load failed', err);
    }
    set({ ready: true });
  },

  loginPin: async (pin) => {
    const { token, user } = await authApi.pin(pin, getDeviceProfile());
    setToken(token);
    set({ user });
  },

  loginUser: async (email, password) => {
    const { token, user } = await authApi.login({ email, password });
    setToken(token);
    set({ user });
  },

  logout: () => {
    setToken(null);
    set({ user: null });
  },

  setTheme: (t) => {
    applyTheme(t);
    set({ theme: t });
  },

  toggleTheme: () => {
    const next = get().theme === 'dark' ? 'light' : 'dark';
    get().setTheme(next);
  },

  refreshCalendars: async () => {
    const { calendars } = await calendarApi.list();
    set({ calendars });
  },

  refreshEvents: async (from, to) => {
    const range = from && to ? { from, to } : monthRange();
    const { events } = await calendarApi.events({
      ...range,
      expand: '1',
    });
    set({ events });
  },

  refreshWidgetsData: async () => {
    const [s, t, m] = await Promise.all([
      notesApi.stickies(),
      notesApi.todos(),
      notesApi.messages(),
    ]);
    set({ stickies: s.stickies, todos: t.todos, messages: m.messages });
  },

  refreshLayout: async () => {
    // Shared family layout — same on PC, phone, and Pi
    const profile = getLayoutProfile();
    const { layout } = await widgetApi.layout(profile);
    set({ layout });
  },

  refreshAlerts: async () => {
    try {
      const { alerts } = await alertApi.active();
      set({ activeAlerts: alerts });
    } catch {
      /* offline ok */
    }
  },

  saveLayout: async (layout) => {
    // Skip no-op updates (prevents blink loops when RGL / WS echo same geometry)
    const prev = get().layout;
    const same =
      prev.length === layout.length &&
      prev.every((p, i) => {
        const n = layout[i];
        return (
          p.i === n.i &&
          p.x === n.x &&
          p.y === n.y &&
          p.w === n.w &&
          p.h === n.h &&
          p.type === n.type
        );
      });
    if (same) return;

    set({ layout });
    if (!get().user) return;
    const profile = getLayoutProfile();
    try {
      await widgetApi.saveLayout(layout, profile);
    } catch (err) {
      console.error('Failed to save widget layout', err);
    }
  },

  setLayoutEditMode: (v) => set({ layoutEditMode: v }),

  openDay: (date) => set({ selectedDate: date, dayPanelOpen: true }),
  closeDay: () => set({ dayPanelOpen: false }),

  openEventEditor: (event = null, defaultDate) => {
    // Always use master event id for edit/delete of recurring series
    let ev = event;
    if (ev?.id?.includes('__')) {
      ev = { ...ev, id: ev.id.split('__')[0] };
    }
    set({ eventEditor: { open: true, event: ev, defaultDate } });
  },
  closeEventEditor: () =>
    set({ eventEditor: { open: false, event: null } }),

  setComposeAlertOpen: (v) => set({ composeAlertOpen: v }),

  sendAlert: async (level, body, title) => {
    const { alert } = await alertApi.create({ level, body, title });
    set({
      activeAlerts: [alert, ...get().activeAlerts.filter((a) => a.id !== alert.id)],
      composeAlertOpen: false,
    });
  },

  dismissAlert: async (id) => {
    // Optimistic remove so UI feels instant
    set({ activeAlerts: get().activeAlerts.filter((a) => a.id !== id) });
    try {
      await alertApi.dismiss(id);
    } catch (err) {
      console.error('Dismiss failed', err);
      void get().refreshAlerts();
    }
  },

  setConnected: (v) => set({ connected: v }),

  applyRealtime: (type, payload) => {
    const p = payload as Record<string, unknown>;
    switch (type) {
      case 'event:created':
      case 'event:updated': {
        // Full refresh keeps recurrence expansion correct
        void get().refreshEvents();
        break;
      }
      case 'event:deleted': {
        const id = p.id as string;
        set({
          events: get().events.filter(
            (e) => e.id !== id && !e.id.startsWith(id + '__')
          ),
        });
        break;
      }
      case 'sticky:updated': {
        const sticky = p as unknown as StickyNote;
        const list = get().stickies.filter((s) => s.id !== sticky.id);
        set({ stickies: [sticky, ...list] });
        break;
      }
      case 'sticky:deleted': {
        set({ stickies: get().stickies.filter((s) => s.id !== p.id) });
        break;
      }
      case 'todo:updated': {
        const todo = p as unknown as Todo;
        const list = get().todos.filter((t) => t.id !== todo.id);
        set({ todos: [todo, ...list].sort((a, b) => a.done - b.done) });
        break;
      }
      case 'todo:deleted': {
        set({ todos: get().todos.filter((t) => t.id !== p.id) });
        break;
      }
      case 'message:created': {
        const msg = p as unknown as Message;
        if (!get().messages.some((m) => m.id === msg.id)) {
          set({ messages: [...get().messages, msg] });
        }
        break;
      }
      case 'alert:created': {
        const alert = p as unknown as FamilyAlert;
        if (!get().activeAlerts.some((a) => a.id === alert.id)) {
          set({ activeAlerts: [alert, ...get().activeAlerts] });
        }
        break;
      }
      case 'alert:dismissed': {
        const id = (p as { id: string }).id;
        set({ activeAlerts: get().activeAlerts.filter((a) => a.id !== id) });
        break;
      }
      case 'layout:updated': {
        // Don't fight the user while they are rearranging widgets
        if (get().layoutEditMode) break;
        const layout = (p as { layout: WidgetLayoutItem[] }).layout;
        if (!layout) break;
        const prev = get().layout;
        const same =
          prev.length === layout.length &&
          prev.every((item, i) => {
            const n = layout[i];
            return (
              item.i === n.i &&
              item.x === n.x &&
              item.y === n.y &&
              item.w === n.w &&
              item.h === n.h
            );
          });
        if (!same) set({ layout });
        break;
      }
      case 'calendar:updated': {
        void get().refreshCalendars();
        break;
      }
      default:
        break;
    }
  },
}));
